import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Partner } from "@/types/partner";
import { BackgroundElements } from "@/components/home/BackgroundElements";
import { 
  ArrowLeft, 
  ExternalLink, 
  FileText, 
  Monitor, 
  MapPin, 
  Users, 
  Zap,
  Building,
  TreePine,
  Eye,
  Target,
  TrendingUp,
  Play,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  X,
  ZoomIn
} from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

const PartnerDetail = () => {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const partnerData: Record<string, Partner & { 
    type: 'indoor' | 'outdoor';
    tagline: string;
    taglineAr: string;
    features: { icon: any; title: string; titleAr: string; description: string; descriptionAr: string }[];
    stats: { value: string; label: string; labelAr: string }[];
    color: string;
    gradient: string;
  }> = {
    tableads: {
      id: "tableads",
      name: "TableAds",
      logo: "/images/5b702a1b-0694-48a1-9c09-9a077bc3c36b.png",
      mediaExample: "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/tableads-demo.mp4",
      images: [],
      website: "https://tableads.net",
      pdfBrochure: "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/tableads-businesscases.pdf",
      pdfPricing: "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/tableads-mediakit.pdf",
      description: t('tableadsDesc'),
      type: 'indoor',
      tagline: "Innovative Table-Top Digital Advertising",
      taglineAr: "إعلانات رقمية مبتكرة على الطاولات",
      color: "#2478C8",
      gradient: "from-primary to-primary/80",
      features: [
        { icon: Monitor, title: "High-Resolution Displays", titleAr: "شاشات عالية الدقة", description: "Crystal clear 4K displays for maximum visual impact", descriptionAr: "شاشات 4K واضحة للغاية لأقصى تأثير بصري" },
        { icon: Target, title: "Captive Audience", titleAr: "جمهور مستهدف", description: "Reach customers during their dining experience", descriptionAr: "الوصول للعملاء أثناء تجربة تناول الطعام" },
        { icon: Zap, title: "Real-Time Updates", titleAr: "تحديثات فورية", description: "Change your content instantly across all locations", descriptionAr: "تغيير المحتوى فوريًا في جميع المواقع" }
      ],
      stats: [
        { value: "500+", label: "Active Screens", labelAr: "شاشة نشطة" },
        { value: "2M+", label: "Monthly Views", labelAr: "مشاهدة شهرية" },
        { value: "150+", label: "Partner Venues", labelAr: "موقع شريك" }
      ]
    },
    alan: {
      id: "alan",
      name: "Alan",
      logo: "/images/48ff5552-81f9-403d-8375-6b7792006fcb.png",
      mediaExample: "",
      images: [
        "/images/7a215971-7ae6-41ff-805f-e2f5fd649415.png"
      ],
      website: "https://alan.sa",
      pdfBrochure: "",
      pdfPricing: "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/alan-mediakit.pdf",
      description: t('alanDesc'),
      type: 'indoor',
      tagline: "Premium Indoor Digital Signage Solutions",
      taglineAr: "حلول اللافتات الرقمية الداخلية المتميزة",
      color: "#2478C8",
      gradient: "from-primary to-primary/80",
      features: [
        { icon: Building, title: "Strategic Locations", titleAr: "مواقع استراتيجية", description: "Premium placements in high-traffic venues", descriptionAr: "مواقع متميزة في أماكن الازدحام العالي" },
        { icon: Eye, title: "High Visibility", titleAr: "رؤية عالية", description: "Eye-level screens for maximum engagement", descriptionAr: "شاشات بمستوى العين لأقصى تفاعل" },
        { icon: Users, title: "Targeted Reach", titleAr: "وصول مستهدف", description: "Connect with your ideal demographic", descriptionAr: "تواصل مع جمهورك المثالي" }
      ],
      stats: [
        { value: "300+", label: "Locations", labelAr: "موقع" },
        { value: "450K+", label: "Daily Impressions", labelAr: "انطباع يومي" },
        { value: "98%", label: "Uptime", labelAr: "وقت التشغيل" }
      ]
    },
    waki: {
      id: "waki",
      name: "Waki",
      logo: "/images/744594d0-c64a-45ae-8c4a-922f8d4ca99f.png",
      mediaExample: "",
      images: [
        "/images/3744477b-be19-44e9-82a1-690e843d42e6.png",
        "/images/a5719610-e1b4-4ac1-9527-94f669b949af.png"
      ],
      website: "https://waki.sa",
      pdfBrochure: "",
      pdfPricing: "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/waki-mediakit.pdf",
      description: t('wakiDesc'),
      type: 'indoor',
      tagline: "Connecting Brands with Young Audiences",
      taglineAr: "ربط العلامات التجارية بالجماهير الشابة",
      color: "#2478C8",
      gradient: "from-primary to-primary/80",
      features: [
        { icon: Users, title: "Youth-Focused", titleAr: "موجه للشباب", description: "Reach Gen Z and millennials effectively", descriptionAr: "الوصول الفعال للجيل زد والألفية" },
        { icon: MapPin, title: "Education Venues", titleAr: "المؤسسات التعليمية", description: "Presence in universities and schools", descriptionAr: "تواجد في الجامعات والمدارس" },
        { icon: TrendingUp, title: "Growing Network", titleAr: "شبكة متنامية", description: "Rapidly expanding screen network", descriptionAr: "شبكة شاشات تتوسع بسرعة" }
      ],
      stats: [
        { value: "85+", label: "School Locations", labelAr: "موقع مدرسي" },
        { value: "50K+", label: "Student Reach", labelAr: "طالب" },
        { value: "300K+", label: "Monthly Impressions", labelAr: "ظهور شهري" }
      ]
    },
    attention: {
      id: "attention",
      name: "Attention",
      logo: "/images/667636f4-bf87-42f0-977e-60656eaaa418.png",
      mediaExample: "",
      images: [
        "/images/attention-example-1.png",
        "/images/attention-example-2.jpg"
      ],
      website: "https://Attention.com.sa",
      pdfBrochure: "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/AttentionProfile.pdf",
      pdfPricing: "",
      description: t('attentionDesc'),
      type: 'outdoor',
      tagline: "Commanding Outdoor Digital Presence",
      taglineAr: "حضور رقمي خارجي مميز",
      color: "#2478C8",
      gradient: "from-primary to-primary/80",
      features: [
        { icon: TreePine, title: "Premium OOH Locations", titleAr: "مواقع خارجية متميزة", description: "Strategic outdoor placements across Saudi Arabia", descriptionAr: "مواقع خارجية استراتيجية في المملكة" },
        { icon: Monitor, title: "Large Format Screens", titleAr: "شاشات كبيرة", description: "High-impact digital billboards and megascreens", descriptionAr: "لوحات رقمية وشاشات ضخمة عالية التأثير" },
        { icon: Eye, title: "Maximum Visibility", titleAr: "أقصى رؤية", description: "24/7 exposure in high-traffic areas", descriptionAr: "تعرض على مدار الساعة في المناطق المزدحمة" }
      ],
      stats: [
        { value: "100+", label: "Outdoor Screens", labelAr: "شاشة خارجية" },
        { value: "750K+", label: "Daily Views", labelAr: "مشاهدة يومية" },
        { value: "15+", label: "Cities Covered", labelAr: "مدينة" }
      ]
    },
    onspot: {
      id: "onspot",
      name: "On Spot",
      logo: "/images/onspot-logo.png",
      mediaExample: "",
      images: [],
      website: "",
      pdfBrochure: "/onspot-pitch-deck.pdf",
      pdfPricing: "",
      description: t('onspotDesc'),
      type: 'indoor',
      tagline: "Digital Advertising in Barbershops & Beauty Salons",
      taglineAr: "إعلانات رقمية في صالونات الحلاقة والتجميل",
      color: "#2478C8",
      gradient: "from-primary to-primary/80",
      features: [
        { icon: Target, title: "Captive Audience", titleAr: "جمهور أسير", description: "Reach customers during grooming & beauty sessions", descriptionAr: "الوصول للعملاء أثناء جلسات الحلاقة والتجميل" },
        { icon: Users, title: "Dual Gender Targeting", titleAr: "استهداف الجنسين", description: "Barbershops for men, beauty salons for women", descriptionAr: "صالونات حلاقة للرجال وصالونات تجميل للنساء" },
        { icon: Monitor, title: "Flexible Formats", titleAr: "أشكال مرنة", description: "Landscape and portrait screen orientations", descriptionAr: "شاشات أفقية وعمودية" }
      ],
      stats: [
        { value: "15", label: "Locations", labelAr: "موقع" },
        { value: "30+", label: "Screens", labelAr: "شاشة" },
        { value: "M & F", label: "Audiences", labelAr: "الجمهور" }
      ]
    }
  };

  const partner = partnerData[partnerId as keyof typeof partnerData];

  if (!partner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold mb-4">{t('partnerDetailsComingSoon')}</h1>
          <p className="mb-6 text-muted-foreground">{t('workingOnAddingInfo')}</p>
          <Button onClick={() => navigate('/partners')} className="bg-primary hover:bg-primary/90">
            {t('backToPartners')}
          </Button>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % partner.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + partner.images.length) % partner.images.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 overflow-hidden">
      <BackgroundElements />

      {/* Back Button */}
      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/partners')}
          className="group text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          {t('backToPartners')}
        </Button>
      </div>

      {/* Hero Section */}
      <section className={`relative py-12 md:py-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Logo & Info */}
            <div className="flex-1 text-center lg:text-left">
              <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${partner.gradient} text-white px-4 py-2 rounded-full text-sm font-semibold mb-6`}>
                {partner.type === 'indoor' ? <Building className="w-4 h-4" /> : <TreePine className="w-4 h-4" />}
                {partner.type === 'indoor' ? t('indoor') : t('outdoor')}
              </div>

              <div className="mb-8">
                <a 
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img 
                    src={partner.logo} 
                    alt={`${partner.name} logo`}
                    className="h-24 md:h-32 w-auto object-contain mx-auto lg:mx-0"
                    style={{
                      imageRendering: '-webkit-optimize-contrast',
                      transform: 'translateZ(0)',
                    }}
                  />
                </a>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
                {partner.name}
              </h1>
              
              <p className={`text-xl md:text-2xl font-medium bg-gradient-to-r ${partner.gradient} bg-clip-text text-transparent mb-6`}>
                {language === 'ar' ? partner.taglineAr : partner.tagline}
              </p>

              <p className="text-lg text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-8">
                {partner.description}
              </p>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg"
                  onClick={() => navigate('/campaign')}
                  className={`bg-gradient-to-r ${partner.gradient} hover:opacity-90 text-white shadow-lg hover:shadow-xl transition-all`}
                >
                  {t('createCampaign')}
                </Button>
                
                {partner.website && (
                  <Button 
                    size="lg"
                    variant="outline"
                    onClick={() => window.open(partner.website, '_blank')}
                    className="border-2 hover:bg-primary/5"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {language === 'ar' ? 'زيارة الموقع' : 'Visit Website'}
                  </Button>
                )}
              </div>
            </div>

            {/* Media Preview */}
            <div className="flex-1 w-full max-w-xl">
              {partner.images && partner.images.length > 0 ? (
                <>
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                    <div className="relative bg-card/80 dark:bg-card/80 backdrop-blur-sm p-4 rounded-2xl shadow-2xl border border-border/30">
                      <AspectRatio ratio={16/9} className="rounded-xl overflow-hidden cursor-pointer" onClick={() => setLightboxOpen(true)}>
                        <img 
                          src={partner.images[currentImageIndex]} 
                          alt={`${partner.name} example ${currentImageIndex + 1}`}
                          className="w-full h-full object-contain transition-transform duration-500 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                          <div className="bg-card/90 dark:bg-card/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                            <ZoomIn className="w-6 h-6 text-foreground" />
                          </div>
                        </div>
                      </AspectRatio>
                      
                      {partner.images.length > 1 && (
                        <>
                          <button 
                            onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-card/90 dark:bg-card/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-card transition-colors z-10"
                          >
                            <ChevronLeft className="w-5 h-5 text-foreground" />
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-card/90 dark:bg-card/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-card transition-colors z-10"
                          >
                            <ChevronRight className="w-5 h-5 text-foreground" />
                          </button>
                          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                            {partner.images.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                                className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-primary w-6' : 'bg-background/60 hover:bg-background/80 dark:bg-foreground/30 dark:hover:bg-foreground/50'}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Lightbox Dialog */}
                  <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                    <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
                      <button
                        onClick={() => setLightboxOpen(false)}
                        className="absolute top-4 right-4 z-50 w-10 h-10 bg-background/10 hover:bg-background/20 dark:bg-foreground/10 dark:hover:bg-foreground/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                      
                      <div className="relative w-full h-[90vh] flex items-center justify-center p-4">
                        <img 
                          src={partner.images[currentImageIndex]} 
                          alt={`${partner.name} example ${currentImageIndex + 1}`}
                          className="max-w-full max-h-full object-contain"
                        />
                        
                        {partner.images.length > 1 && (
                          <>
                            <button 
                              onClick={prevImage}
                              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/10 hover:bg-background/20 dark:bg-foreground/10 dark:hover:bg-foreground/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
                            >
                              <ChevronLeft className="w-6 h-6 text-white" />
                            </button>
                            <button 
                              onClick={nextImage}
                              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/10 hover:bg-background/20 dark:bg-foreground/10 dark:hover:bg-foreground/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
                            >
                              <ChevronRight className="w-6 h-6 text-white" />
                            </button>
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                              {partner.images.map((_, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setCurrentImageIndex(idx)}
                                  className={`w-3 h-3 rounded-full transition-all ${idx === currentImageIndex ? 'bg-background dark:bg-foreground w-8' : 'bg-background/40 hover:bg-background/60 dark:bg-foreground/20 dark:hover:bg-foreground/30'}`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              ) : partner.mediaExample ? (
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/30">
                    <AspectRatio ratio={16/9}>
                      <video 
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        disablePictureInPicture
                        controls={false}
                        controlsList="nodownload nofullscreen noremoteplayback"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        <source src={partner.mediaExample} type="video/mp4" />
                      </video>
                    </AspectRatio>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-16 h-16 bg-background/20 dark:bg-foreground/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-white fill-white" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`relative py-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {partner.stats.map((stat, index) => (
              <div 
                key={index}
                className="relative group"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${partner.gradient} rounded-2xl opacity-10 group-hover:opacity-20 transition-opacity`}></div>
                <div className="relative p-8 rounded-2xl border border-border/30 bg-card/80 dark:bg-card/80 backdrop-blur-sm text-center hover:shadow-xl transition-all">
                  <h3 className={`text-4xl md:text-5xl font-bold bg-gradient-to-r ${partner.gradient} bg-clip-text text-transparent mb-2`}>
                    {stat.value}
                  </h3>
                  <p className="text-lg text-muted-foreground font-medium">
                    {language === 'ar' ? stat.labelAr : stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`relative py-16 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
              {language === 'ar' ? 'لماذا تختار' : 'Why Choose'} {partner.name}?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partner.features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div 
                  key={index}
                  className="group relative p-8 rounded-2xl border border-border/30 bg-card/80 dark:bg-card/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className={`w-16 h-16 bg-gradient-to-r ${partner.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {language === 'ar' ? feature.titleAr : feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {language === 'ar' ? feature.descriptionAr : feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      {(partner.pdfBrochure || partner.pdfPricing) && (
        <section className={`relative py-16 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                {language === 'ar' ? 'الوثائق والموارد' : 'Documents & Resources'}
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              {partner.pdfBrochure && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      size="lg"
                      variant="outline"
                      className="group border-2 hover:border-primary hover:bg-primary/5 transition-all px-8 py-6 h-auto"
                    >
                      <FileText className="mr-3 h-6 w-6 group-hover:text-primary transition-colors" />
                      <span className="text-lg font-medium">
                        {partnerId === 'attention' ? 'View English Profile' : t('viewBusinessCases')}
                      </span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-5xl h-[85vh]">
                    <iframe
                      src={partner.pdfBrochure}
                      className="w-full h-full rounded-lg"
                      title={`${partner.name} Profile`}
                    />
                  </DialogContent>
                </Dialog>
              )}

              {partnerId === 'attention' && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      size="lg"
                      variant="outline"
                      className="group border-2 hover:border-primary hover:bg-primary/5 transition-all px-8 py-6 h-auto"
                    >
                      <FileText className="mr-3 h-6 w-6 group-hover:text-primary transition-colors" />
                      <span className="text-lg font-medium">View Arabic Profile</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-5xl h-[85vh]">
                    <iframe
                      src="https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/AttentionProfileAR.pdf"
                      className="w-full h-full rounded-lg"
                      title={`${partner.name} Arabic Profile`}
                    />
                  </DialogContent>
                </Dialog>
              )}

              {partner.pdfPricing && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      size="lg"
                      variant="outline"
                      className="group border-2 hover:border-primary hover:bg-primary/5 transition-all px-8 py-6 h-auto"
                    >
                      <FileText className="mr-3 h-6 w-6 group-hover:text-primary transition-colors" />
                      <span className="text-lg font-medium">{t('viewMediaKit')}</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-5xl h-[85vh]">
                    <iframe
                      src={partnerId === 'waki' ? "https://coydcdtajowjqzcjqrpc.supabase.co/storage/v1/object/public/media/waki-mediakit.pdf" : partner.pdfPricing}
                      className="w-full h-full rounded-lg"
                      title={`${partner.name} Media Kit`}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`relative p-8 md:p-16 rounded-3xl overflow-hidden bg-gradient-to-r ${partner.gradient}`}>
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute inset-0">
              <div className="absolute top-0 left-0 w-96 h-96 bg-background/10 dark:bg-foreground/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-background/10 dark:bg-foreground/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative z-10 text-center text-white">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                {t('readyToReach')}
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                {t('startYourDigital')} {partner.name} {language === 'ar' ? 'وابدأ رحلتك الإعلانية اليوم' : 'and start your advertising journey today'}
              </p>
              <Button 
                size="lg"
                onClick={() => navigate('/campaign')}
                className="bg-background/90 text-foreground hover:bg-background/80 dark:bg-card/90 dark:hover:bg-card/80 shadow-xl hover:shadow-2xl transition-all px-10 py-6 text-lg font-semibold"
              >
                {t('createCampaign')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerDetail;
